// Schedule utility functions and constants

export const days = [
  "Montag",
  "Dienstag",
  "Mittwoch",
  "Donnerstag",
  "Freitag",
  "Samstag",
];

export const hours = Array.from({ length: 12 }, (_, i) => 10 + i);

// Get hours where at least one coach is available (for a specific day or all days)
export const getAvailableHours = (coaches, day = null) => {
  const hoursSet = new Set();

  coaches.forEach((coach) => {
    if (Array.isArray(coach.availableTimes)) {
      coach.availableTimes.forEach((timeSlot) => {
        const parts = String(timeSlot).trim().split(" ");
        if (parts.length === 2) {
          const [timeDay, hourStr] = parts;
          const hour = parseInt(hourStr, 10);

          // If filtering by day, only include hours for that day
          // Otherwise, include all hours from all days
          if (day === null || timeDay === day) {
            if (!isNaN(hour)) {
              hoursSet.add(hour);
            }
          }
        }
      });
    }
  });

  // Convert Set to sorted array
  return Array.from(hoursSet).sort((a, b) => a - b);
};

// Helper function to chunk array into smaller arrays
export const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Helper function to get icon for children based on trainigGroup
export const getChildIcon = (student) => {
  if (student.adult) return ""; // Adults get no icon

  const groupColorMap = {
    Kinderland: "🩷",
    Rot: "🔴",
    Grün: "🟢",
    Orange: "🟠",
    "Gelb Team": "🟡",
    "Gelb Hobby": "🟡",
  };

  return groupColorMap[student.trainigGroup] || "⚪"; // Default white circle if no group
};

// Helper to group students by coach
// If day/hour provided, use coach from that specific assignment
// Otherwise fall back to legacy student.coach field
export const groupStudentsByCoach = (studentsList, day = null, hour = null) => {
  const groups = {};

  studentsList.forEach((student) => {
    let coachId = 'unassigned';

    // If day/hour specified, find coach from that specific assignment
    if (day !== null && hour !== null && Array.isArray(student.assignments)) {
      const assignment = student.assignments.find((a) =>
        String(a.day).trim() === String(day).trim() &&
        Number(a.hour) === hour
      );
      if (assignment && assignment.coach) {
        coachId = String(assignment.coach).trim();
      }
    } else {
      // Fall back to legacy student.coach field
      coachId = student.coach ? String(student.coach).trim() : 'unassigned';
    }

    if (!groups[coachId]) {
      groups[coachId] = [];
    }
    groups[coachId].push(student);
  });

  return groups;
};

// Calculate students in cells for schedule
// Updated to support multiple assignments per student
export const calculateStudentsInCells = (students, days, hours) =>
  days
    .map((day) =>
      hours.map((hour) => {
        const studentsInCell = students
          .filter((s) => {
            if (!s) return false;

            // Check if student has assignments array (new format)
            if (Array.isArray(s.assignments) && s.assignments.length > 0) {
              return s.assignments.some((assignment) => {
                return (
                  String(assignment.day).trim() === String(day).trim() &&
                  Number(assignment.hour) === hour
                );
              });
            }

            // Fall back to legacy fields (old format) for backward compatibility
            if (String(s.day).trim() !== String(day).trim()) return false;
            const sHour =
              s.hour === null || s.hour === undefined ? null : Number(s.hour);
            return sHour === hour;
          })
          .map((s) => s._id);
        return { day, hour, students: studentsInCell };
      })
    )
    .flat();

// Coach helper functions
export const findCoachByIdOrName = (coaches, val) => {
  if (!val) return null;
  const v = String(val).trim();
  const byId = coaches.find((c) => String(c._id) === v);
  if (byId) return byId;
  return coaches.find((c) => `${c.firstName} ${c.lastName}` === v) || null;
};

export const coachMatchesStudent = (coach, student) => {
  if (!student) return false;
  if (!coach) return !student.coach; // if no coach column provided, only show unassigned students
  if (!student.coach) return false; // unassigned students don't show in any coach column
  const sVal = String(student.coach).trim();
  return (
    String(coach._id) === sVal ||
    `${coach.firstName} ${coach.lastName}` === sVal
  );
};

// New version that checks assignment coach for specific day/hour
export const coachMatchesStudentAssignment = (coach, student, day, hour) => {
  if (!student) return false;

  // Check if student has assignments array
  if (Array.isArray(student.assignments) && student.assignments.length > 0) {
    // Find the assignment for this specific day/hour
    const assignment = student.assignments.find((a) =>
      String(a.day).trim() === String(day).trim() &&
      Number(a.hour) === hour
    );

    if (!assignment) return false; // Student doesn't have assignment at this time

    if (!coach) return !assignment.coach; // No coach column = show unassigned only
    if (!assignment.coach) return false; // Unassigned students don't show in coach columns

    const assignmentCoachId = String(assignment.coach).trim();
    return (
      String(coach._id) === assignmentCoachId ||
      `${coach.firstName} ${coach.lastName}` === assignmentCoachId
    );
  }

  // Fall back to legacy coachMatchesStudent for backward compatibility
  return coachMatchesStudent(coach, student);
};

export const coachesForDay = (coaches, day) =>
  coaches.filter(
    (coach) =>
      Array.isArray(coach.availableTimes) &&
      coach.availableTimes.some((t) => String(t).startsWith(day))
  );

// Check if coach is available at specific day and hour
export const isCoachAvailable = (coach, day, hour) => {
  if (!coach || !day || hour === null || hour === undefined) return false;

  if (!Array.isArray(coach.availableTimes)) return false;

  const timeSlot = `${day} ${hour}`;
  return coach.availableTimes.some((t) => String(t).trim() === timeSlot);
};

// Normalize a single student object (convert types, trim strings, handle nulls)
export const normalizeStudent = (student) => ({
  ...student,
  coach: student.coach ? String(student.coach).trim() : "",
  day: typeof student.day === "string" ? student.day.trim() : student.day,
  hour:
    student.hour === null || student.hour === undefined || student.hour === ""
      ? null
      : Number(student.hour),
  availableTimes: Array.isArray(student.availableTimes)
    ? student.availableTimes
    : [],
});

// Normalize array of students
export const normalizeStudents = (students) => students.map(normalizeStudent);
